import { T_Product, T_Coupon, T_Order, T_OrderProduct, T_PPV, NullableDate, T_Customer, T_Profile } from './types';
import { AFFILIATION, productSkuIds, SPAs } from './config';
import { useCategory } from './helpers/useBrand';
import { removeNullValues } from './entry';
import SHA256 from 'crypto-js/sha256';

export const SegmentECommerceEvents = {
  ADD_TO_CART: 'Add to Cart',
  PRODUCT_ADDED: 'Product Added',
  PRODUCT_REMOVED: 'Product Removed',
  CHECKOUT_STARTED: 'Checkout Started',
  ORDER_COMPLETED: 'Order Completed',
  EVENT_PREQUAL_ADDRESS_CHECK: 'Broadband Availability Checked',
  RETURN_TO_CART: 'Cart Viewed',
  PROMOTION_CLICKED: 'Promotion Clicked',
  LABEL_AND_URL: 'Cta Clicked',
  SIGNED_IN: 'Signed In',
  PAYMENT_INFO_ENTERED: 'Payment Info Entered',
  CHECKOUT_STEP_COMPLETED: 'Checkout Step Completed',
  VTV_MIGRATION_COMPLETE: 'VTV Migration Complete',
  PRODUCT_EXPLORED: 'Promotion Clicked',
  PRODUCT_CANCELLED: 'Product Cancelled',
  TOGGLE_CLICK: 'Toggle Clicked',
};

export const SKY_BRAND = 'Sky';
export const PPV = 'PPV';
export const BROADBAND = 'BROADBAND';
export const BUNDLE_OFFER = 'BUNDLE_OFFER';
export const ANNIVERSARY_OFFER = 'ANNIVERSARY_OFFER';
export const GET_OFFER = 'Get Offer';

export const EventNames = {
  BUTTON_CLICK: 'Button Click',
  POPUP_VIEW: 'Popup Viewed',
  PRODUCT_SELECTED: 'Product Selected',
}

export const BroadbandFunnelTypes = {
  FIBRE_SWAP: 'fibre swap',
  INTACT_ONT: 'intact ONT',
  NEW_FIBRE: 'new fibre',
};

export const QuizEvents = {
  QUIZ_LAUNCHED: 'Quiz Launched',
  QUIZ_INTERACTION: 'Quiz Interaction',
  QUIZ_COMPLETED: 'Quiz Completed',
};

export const QuizInteractionTypes = {
  LAUNCH: 'launch',
  PAGE_VIEW: 'page view',
  BUTTON_CLICK: 'button click',
  ADDRESS_ENTERED: 'address entered',
  FINISH: 'finish',
  NA: 'n/a',
};

export const FaultSwapEvents = {
  BUTTON_CLICK: 'button click',
  TRACK_ISSUES_WITH_THE_DEVICE: 'track issues with the device',
  ORDER_COMPLETED: 'Fault Swap Order Completed',
};
export const BookATechEvents = {
  tech_service_continue_clicked: 'tech_service_continue_clicked',
  Confirm_Details: 'confirm_details_clicked',
  Submit: 'submit_clicked',
  Rechedule: 'reschedule_submit_clicked',
  confirm_cancellation_clicked: 'confirm_cancellation_clicked',
};
export const SatelliteMigrationEvent = {
  verification_submit_clicked: 'verification_submit_clicked',
  prize_link_clicked: 'prize_link_clicked',
  book_tech_clicked: 'book_tech_clicked',
  video_Tracking: 'video_Tracking',
};
export const VoucherEvent = {
  voucher_apply_clicked: 'voucher_apply_clicked',
  voucher_remove_clicked: 'voucher_remove_clicked',
};

class SClient {
  private default_Value: any | null = '_null_';

  private customerDetails: any | null = null;
  private profileDetails: any | null = null;
  private billingDetails: any | null = null;

  private traitsDetails: any | null = null;
  private eventGlobals: Record<string, any> = {};

  private city: string | null = null;
  private country: string | null = null;
  private account_id: string | null = null;
  private emailId: string | null = null;

  private anonymousUserDetails: any | null = null;

  constructor() {
    this.initializeUserLocation(); // Initialize user location
  }

  async initializeUserLocation() {
    try {
      const response = await fetch('https://ipinfo.io/json');
      const data = await response.json();
      this.city = data.city || this.default_Value;
      this.country = data.country || this.default_Value;
    } catch (error) {
      console.error('Error fetching location:', error);
      this.city = this.default_Value; // Default to unknown if there's an error
      this.country = this.default_Value; // Default to unknown if there's an error
    }
  }

  getCurrencyCodeBasedOnLocation(): string {
    const currencyMap: { [key: string]: string } = {
      US: 'USD',
      NZ: 'NZD',
      AU: 'AUD',
      IN: 'INR',
      GB: 'GBP',
    };
    // @ts-ignore
    return currencyMap[this.country || this.default_Value] || this.default_Value;
  }
  getDeviceInfo(): { deviceType: string; os: string; osVersion: string; browser: string } {
    const userAgent = navigator.userAgent.toLowerCase();
    const maxTouchPoints = navigator.maxTouchPoints || 0;
    let deviceType = 'desktop';
    let os = 'unknown';
    let osVersion = 'unknown';
    let browser = 'unknown';

    // Detect Device Type
    if (/mobile|android|iphone|ipod/i.test(userAgent)) {
      deviceType = 'mobile';
    } else if (/ipad|tablet/i.test(userAgent) || (maxTouchPoints > 1 && /macintosh/i.test(userAgent))) {
      deviceType = 'tablet';
    }

    // Detect OS and OS Version
    if (/windows nt 10/i.test(userAgent)) {
      os = 'Windows';
      osVersion = '10';
    } else if (/windows nt 6.3/i.test(userAgent)) {
      os = 'Windows';
      osVersion = '8.1';
    } else if (/windows nt 6.2/i.test(userAgent)) {
      os = 'Windows';
      osVersion = '8';
    } else if (/windows nt 6.1/i.test(userAgent)) {
      os = 'Windows';
      osVersion = '7';
    } else if (/macintosh|mac os x/i.test(userAgent)) {
      os = 'macOS';
      osVersion = /mac os x (\d+[\._]\d+)/.test(userAgent) ? RegExp.$1.replace('_', '.') : 'unknown';
    } else if (/android/i.test(userAgent)) {
      os = 'Android';
      osVersion = /android (\d+(\.\d+)?)/.test(userAgent) ? RegExp.$1 : 'unknown';
    } else if (/iphone|ipad|ipod/i.test(userAgent)) {
      os = 'iOS';
      osVersion = /os (\d+[\._]\d+)/.test(userAgent) ? RegExp.$1.replace('_', '.') : 'unknown';
    } else if (/linux/i.test(userAgent)) {
      os = 'Linux';
    }

    // Detect Browser
    if (/chrome|crios/i.test(userAgent) && !/edge|opr/i.test(userAgent)) {
      browser = 'Chrome';
    } else if (/firefox|fxios/i.test(userAgent)) {
      browser = 'Firefox';
    } else if (/safari/i.test(userAgent) && !/chrome|crios/i.test(userAgent)) {
      browser = 'Safari';
    } else if (/edge/i.test(userAgent)) {
      browser = 'Edge';
    } else if (/opr|opera/i.test(userAgent)) {
      browser = 'Opera';
    } else if (/msie|trident/i.test(userAgent)) {
      browser = 'Internet Explorer';
    }

    return { deviceType, os, osVersion, browser };
  }

  getQueryString(): string | null {
    const queryString = window.location.search; // Get the query string from the URL
    return queryString ? queryString : null; // Return query string if present, otherwise null
  }
  //https://account.sky.co.nz/?utm_source=sfmc&utm_medium=email&utm_content=SR_MovieUnlock_Wrap_NoViewership_Upgrade&utm_campaign=SR_MovieUnlock_Wrap_NoViewership_Upgrade
  private getUtmParameter(param: string): string | null {
    const queryString = this.getQueryString();
    if (queryString) {
      const urlParams = new URLSearchParams(queryString);
      return urlParams.get(param) || this.default_Value;
    }
    return this.default_Value;
  }

  // async storeResult(): Promise<void> {
  //   const segmentFromWindowObject = (window as any).analytics;
  //   try {
  //     this.traitsDetails = await segmentFromWindowObject.identify();
  //     if (this.traitsDetails !== null) {
  //       this.account_id = this.traitsDetails?.event?.traits?.account_id || null;
  //       this.emailId = this.traitsDetails?.event?.traits?.email || null;
  //     }
  //   } catch (error) {
  //     console.error('Error:', error);
  //   }
  // }

  // useGlobalResult(): void {
  //   if (this.traitsDetails !== null && this.account_id !== null) {
  //     this.account_id = this.traitsDetails?.event?.traits?.account_id || null;
  //     this.emailId = this.traitsDetails?.event?.traits?.email || null;
  //   } else {
  //     const {account_id,email} = JSON.parse(localStorage.getItem('ajs_user_traits') ?? '{}');
  //     this.account_id = account_id || null;
  //     this.emailId = email || null;
  //   }
  // }

  getTraitsUserDetails() {
    if (this.traitsDetails !== null && this.account_id !== null) {
      this.account_id = this.traitsDetails?.event?.traits?.account_id || this.default_Value;
      this.emailId = this.traitsDetails?.event?.traits?.email || this.default_Value;
    } else {
      const { account_id, email } = JSON.parse(localStorage.getItem('ajs_user_traits') ?? '{}');
      this.account_id = account_id || this.default_Value;
      this.emailId = email || this.default_Value;
    }
  }

  async getData(): Promise<void> {
    try {
      const response = await fetch('https://ipapi.co/json/');
      let data = await response.json();
      if (data !== null) {
        this.city = data?.city || this.default_Value;
        this.country = data?.country || this.default_Value;
      }
    } catch (error) {
      console.error('Error in fetching user data:', error);
    }
  }

  setCustomerDetails(data: any) {
    this.customerDetails = data;
  }
  setProfileDetails(data: any) {
    this.profileDetails = data;
  }
  setProductsData = (data: any) => {
    this.billingDetails = data;
  };

  setTraitsDetails(data: any) {
    this.traitsDetails = data;
  }

  setEventGlobals(data: Record<string, any>) {
    this.eventGlobals = {
      ...this.eventGlobals,
      ...data,
    };
  }

  getProductsData(): { campaignPromotionCodes: any; productSKUs: any } {
    let products = this.billingDetails?.billing?.products ?? [];
    let campaigns = this.billingDetails?.billing?.campaigns ?? [];
    // const campaignPromotionCodes = campaigns.map((campaign: {promotionDescription: any; promotionCode: any;}) => ({
    //   promotionCode: campaign.promotionCode,
    //   promotionDescription: campaign.promotionDescription,
    // }))
    // Convert array to string format "promotionCode:promotionDescription"
    const campaignPromotionCodes = campaigns
      .map(
        (campaign: { promotionCode: any; promotionDescription: any }) =>
          `${campaign?.promotionCode || ''}:${campaign?.promotionDescription || ''}`
      )
      .join(', ');

    // const productSKUs = products.map((product: {name: any; sku: any; }) => ({
    //   sku: product?.sku,
    //   name: product.name,
    // }));
    // Group products by categoryName
    const productByCategoryName = products.reduce(
      (acc: { [x: string]: string[] }, product: { categoryName: string; sku: any; name: any }) => {
        const category = product.categoryName || ''; // Handle missing category names
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(`'${product?.sku}-${product.name}'`);
        return acc;
      },
      {}
    );

    // Convert grouped object to a formatted string
    const productSKUs = Object.entries(productByCategoryName)
      .map(([category, items]: [String, any]) => `${category}: ${items.join(', ')}`)
      .join('\n');

    return { campaignPromotionCodes, productSKUs };
  }

  getHasCustomerMobileNo() {
    const primaryDetails =
      this.customerDetails?.Parties?.find((p: { id: string }) => p.id === '1')?.types['MP:Primary'] || undefined;
    let phoneNumber = primaryDetails?.contactDetails?.[0];
    if (this.anonymousUserDetails?.userPhone && phoneNumber == undefined) {
      phoneNumber = this.anonymousUserDetails?.userPhone ?? undefined;
    }
    const hashedPhoneNumber = phoneNumber ? SHA256(phoneNumber).toString() : this.default_Value;
    return hashedPhoneNumber;
  }

  getHasEmail() {
    let hasEmail = this.profileDetails?.email ?? undefined;
    if (this.anonymousUserDetails?.email && hasEmail == undefined) {
      hasEmail = this.anonymousUserDetails?.email ?? undefined;
    }
    hasEmail = hasEmail ? SHA256(hasEmail).toString() : this.default_Value;
    return hasEmail;
  }

  getDateOfBirth() {
    let dateOfBirth = this.customerDetails?.Parties?.[0].types['MP:Primary']?.personalDetails?.dob ?? undefined;
    if (this.anonymousUserDetails?.date_of_birth && dateOfBirth == undefined) {
      dateOfBirth = this.anonymousUserDetails?.date_of_birth ?? undefined;
    }
    return dateOfBirth ?? this.default_Value;
  }

  getPageProperties(eventType: string) {
    return {
      userData: this.getTraitsUserDetails(),
      title: document.title, // Page title
      url: window.location.href, // Full URL of the page
      previousPage: document.referrer,
      path: window.location.pathname, // Path (e.g., /home, /about)
      referrer: document.referrer, // Referrer (if available)
      browserHeight: document.documentElement.clientHeight,
      browserWidth: document.documentElement.clientWidth,
      browserName: this.getDeviceInfo().browser,
      currentPage: window.location.href,
      siteLanguage: navigator.language,
      deviceType: this.getDeviceInfo().deviceType,
      environment: process.env.REACT_APP_DEPLOY_ENV,
      operatingSystem: `${this.getDeviceInfo().os} - ${this.getDeviceInfo().osVersion}`,
      domain: window.location.hostname,
      timeStampUTC: new Date().toISOString(),
      currencyCode: this.getCurrencyCodeBasedOnLocation(),
      queryString: this.getQueryString() || '',
      accountId: this.profileDetails?.accountNumber || this.default_Value,
      accountStatus: this.account_id ? 'subscriber' : 'anonymous',
      isLoggedIn: this.account_id ? 'true' : 'false',
      userId: this.account_id || this.anonymousUserDetails?.email || this.default_Value,
      loginStatus: this.account_id ? 'logged in' : 'logged out',
      e_domainName: this.emailId?.split('@')[1],
      e_userName: this.emailId?.split('@')[0],
      utm_campaign: this.getUtmParameter('utm_campaign') || this.default_Value,
      utm_content: this.getUtmParameter('utm_content') || this.default_Value,
      utm_medium: this.getUtmParameter('utm_medium') || this.default_Value,
      utm_source: this.getUtmParameter('utm_source') || this.default_Value,
      utm_term: this.getUtmParameter('utm_term') || this.default_Value,
      userCreatedAt: this.profileDetails?.createdAt,
      userAddress: removeNullValues({
        ...this.customerDetails?.serviceAddress?.primaryAddress,
        tuiAddressCode: this.customerDetails?.tuiAddressCode || '',
      })
        ? Object.entries(
            removeNullValues({
              ...this.customerDetails?.serviceAddress?.primaryAddress,
              tuiAddressCode: this.customerDetails?.tuiAddressCode || '',
            })
          )
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ')
        : this.default_Value,
      userModifiedAt: this.profileDetails?.modifiedAt,
      userGroups: this.profileDetails?.groups?.join(', ') ?? this.default_Value,
      userCity: this.city || this.default_Value,
      userCountry: this.country || this.default_Value,
      userTvPackages: this.getProductsData().productSKUs || this.default_Value,
      userCampaign: this.getProductsData().campaignPromotionCodes || this.default_Value,
      userMonthlyCharge: this.billingDetails?.billing?.monthlyCharge ?? this.default_Value,
      usercurrentTotalAmountDue: this.billingDetails?.billing?.currentTotalAmountDueCents ?? this.default_Value, //Account Outstanding
      userPaymentDue:
        (this.billingDetails?.billing?.currentTotalAmountDueCents ?? null) === null
          ? this.default_Value
          : this.billingDetails?.billing?.currentTotalAmountDueCents > 0
          ? 'no'
          : 'yes',
      eventType: eventType,
      userType: this.profileDetails?.groups.join(','),
      userFirstName: this.profileDetails?.firstName || this.anonymousUserDetails?.firstName || this.default_Value,
      userLastName: this.profileDetails?.lastName || this.anonymousUserDetails?.lastName || this.default_Value,
      userPhoneHashed: this.getHasCustomerMobileNo(),
      userEmailHashed: this.getHasEmail(),
      date_of_birth: this.getDateOfBirth(),
    };
  }

  // change only diskLess nsb box name to `New Sky Box - No MySky`
  getProductName(name: string, sku?: string ) {
    return sku === productSkuIds.arrowBoxDiskless.primary ? 'New Sky Box - No MySky' : name;
  }

  track(event: string, properties?: any, options?: any, callback?: any) {
    let segmentFromWindowObject = (window as any).analytics;
    if (!segmentFromWindowObject) {
      return;
    }

    try {
      const { global: eventGlobal, ...restProperties } = properties ?? {};
      // Merge the page data with the custom properties
      const finalProperties = {
        ...this.getPageProperties('track'), // Include page-related properties
        ...this.eventGlobals,
        ...(eventGlobal ?? {}),
        ...restProperties, // Include any custom properties provided
      };

      // Track the event with the merged properties
      segmentFromWindowObject.track(event, finalProperties, options, callback);
    } catch (err) {
      console.error('Error tracking event:', err);
    }
  }

  identify(accountId: string, email: string, dthAccountNumber: string, profileId: string, identifyPayload?: any) {
    let segmentFromWindowObject = (window as any).analytics;
    if (!segmentFromWindowObject) {
      console.error('Segment disabled.');
      return;
    }

    let traits = {
      account_id: accountId,
      email: email,
      dth_account_number: dthAccountNumber,
      profile_id: profileId,
      // ...(identifyPayload && {
      //   userType: identifyPayload?.customerGroupTypesString ?? "CUSTOMER",
      //   userEmailHashed: identifyPayload?.emailHashed,
      //   userPhoneHashed: identifyPayload?.userPhoneHashed,
      //   userFirstName: identifyPayload?.firstName,
      //   userLastName: identifyPayload?.lastName,
      //   date_of_birth: identifyPayload?.dob
      // })
    };
    try {
      segmentFromWindowObject.identify(accountId, traits);
      this.setTraitsDetails(traits);
    } catch (err) {
      console.error(err);
    }
  }

  identifyAnonymousUser(email: string, firstName: string, dob: NullableDate, identifyPayload?: any) {
    let segmentFromWindowObject = (window as any).analytics;
    if (!segmentFromWindowObject) {
      console.error('Segment disabled.');
      return;
    }
    let traits = {
      email: email,
      firstname: firstName,
      userId: email,
      date_of_birth: dob,
      // ...(identifyPayload && {
      //   userType: identifyPayload?.customerGroupTypesString ?? "CUSTOMER",
      //   userEmailHashed: identifyPayload?.emailHashed,
      //   userPhoneHashed: identifyPayload?.userPhoneHashed,
      //   userFirstName: identifyPayload?.firstName,
      //   userLastName: identifyPayload?.lastName,
      // })
    };
    this.anonymousUserDetails = { ...(identifyPayload || {}), ...(traits || {}) };

    try {
      segmentFromWindowObject.identify(email, traits);
      this.setTraitsDetails(traits);
    } catch (err) {
      console.error(err);
    }
  }

  resetIdentity() {
    let segmentFromWindowObject = (window as any).analytics;
    if (!segmentFromWindowObject) {
      console.error('Segment disabled.');
      return;
    }
    try {
      segmentFromWindowObject?.reset();
      this.setTraitsDetails([]);
    } catch (err) {
      console.error(err);
    }
  }

  page() {
    let segmentFromWindowObject = (window as any).analytics;
    if (!segmentFromWindowObject) {
      console.error('Segment disabled.');
      return;
    }
    try {
      const pageProperties = this.getPageProperties('page');
      const finalProperties = {
        ...pageProperties, // Include page-related properties
        ...this.eventGlobals,
      };
      segmentFromWindowObject.page('Page', finalProperties);
    } catch (err) {
      console.error(err);
    }
  }
  //BB Added or Removed
  bbProductAddOrRemove(product: any, isUpgrade: boolean, quantity = 1, price?: any, isBBAdded?: boolean) {
    this.track(isBBAdded ? SegmentECommerceEvents.PRODUCT_ADDED : SegmentECommerceEvents.PRODUCT_REMOVED, {
      cart_type: isUpgrade ? AFFILIATION.UPG : AFFILIATION.ACQ,
      package_type: BROADBAND,
      brand: SKY_BRAND,
      category: useCategory(product.categoryId),
      name: this.getProductName(product.name, product?.sku),
      price: price,
      quantity,
      id: product?.sku,
      variant: product.categoryName,
      is_segment: 'yes',
    });
  }

  //GA for Product Addition
  productAdded(product: T_Product, isUpgrade: boolean, quantity = 1, packageType?: string) {
    this.track(SegmentECommerceEvents.PRODUCT_ADDED, {
      cart_type: isUpgrade ? AFFILIATION.UPG : AFFILIATION.ACQ,
      package_type: packageType ? packageType : useCategory(product.categoryId),
      brand: SKY_BRAND,
      category: useCategory(product.categoryId),
      name: this.getProductName(product.name, product?.sku),
      price: product.price0,
      quantity,
      id: product?.sku,
      variant: product.categoryName,
      is_segment: 'yes',
    });
  }
  //Exploring BB
  exploreBB(product: any, price?: any, explore?: boolean) {
    this.track(SegmentECommerceEvents.PRODUCT_EXPLORED, {
      cart_type: 'upgrade',
      package_type: BROADBAND,
      brand: SKY_BRAND,
      category: BROADBAND,
      name: product?.name,
      price: price,
      quantity: 1,
      id: product?.sku,
      variant: product?.categoryName,
      is_segment: 'yes',
    });
  }

  //BB Add to Cart Handled
  BBCheckout(product: any, isUpgrade: boolean) {
    this.track(SegmentECommerceEvents.PRODUCT_ADDED, {
      products_added: product,
      cart_type: isUpgrade ? AFFILIATION.UPG : AFFILIATION.ACQ,
      package_type: BROADBAND,
      brand: SKY_BRAND,
      quantity: 1,
      is_segment: 'yes',
    });
  }

  // User Clicks 'Book Now' on PPV Movie Product on My Account Dashboard
  ppvAdded(product: T_PPV) {
    this.track(SegmentECommerceEvents.PRODUCT_ADDED, {
      cart_type: 'upgrade',
      package_type: PPV,
      brand: SKY_BRAND,
      category: PPV,
      name: product.title,
      price: product.offerings[0].price / 100,
      quantity: 1,
      id: product.id,
      variant: product.group,
      is_segment: 'yes',
    });
  }

  //Anniversary Offer Banner Click in myaccount Dashboard
  getAnniversaryOfferDetails(offerPage: any) {
    this.track(SegmentECommerceEvents.PROMOTION_CLICKED, {
      cart_type: 'upgrade',
      package_type: ANNIVERSARY_OFFER,
      brand: SKY_BRAND,
      category: ANNIVERSARY_OFFER,
      name: GET_OFFER,
      url: offerPage,
      quantity: 1,
      is_segment: 'yes',
    });
  }

  //Bundle Offer Banner Click in myaccount Dashboard
  getOfferDetails(offerPrice: any, offerPage: any) {
    this.track(SegmentECommerceEvents.PROMOTION_CLICKED, {
      cart_type: 'upgrade',
      package_type: BUNDLE_OFFER,
      brand: SKY_BRAND,
      category: BUNDLE_OFFER,
      name: GET_OFFER,
      url: offerPage,
      price: offerPrice,
      quantity: 1,
      is_segment: 'yes',
    });
  }

  ppvBooked(
    orderId: number,
    sku: string,
    name: string,
    price: number,
    total: number,
    affiliation: string,
    funnelType: string,
    brand: string,
    coupon?: string,
    variant?: string
  ) {
    this.track(SegmentECommerceEvents.ORDER_COMPLETED, {
      cart_type: affiliation,
      package_type: PPV,
      coupon: coupon,
      value: Number((total ?? 0).toFixed(2)),
      order_id: orderId ? orderId + '' : '',
      is_segment: 'yes',
      products: [
        {
          brand: SKY_BRAND,
          category: PPV,
          name: name,
          price: price,
          quantity: 1,
          id: sku,
          variant: variant,
        },
      ],
    });
  }

  productRemoved(product: T_Product, isUpgrade: boolean, quantity = 1, packageType?: string) {
    this.track(SegmentECommerceEvents.PRODUCT_REMOVED, {
      cart_type: isUpgrade ? AFFILIATION.UPG : AFFILIATION.ACQ,
      package_type: packageType ? packageType : useCategory(product.categoryId),
      brand: SKY_BRAND,
      category: useCategory(product.categoryId),
      name: this.getProductName(product.name, product?.sku),
      price: product.price0,
      quantity,
      id: product?.sku,
      variant: product.categoryName,
      is_segment: 'yes',
    });
  }

  // User Starts Checkout by Clicking the 'Checkout Button' and Landing on https://cart.sky.co.nz/checkout/account-details
  checkoutStarted(
    order: T_Order,
    isUpgrade: boolean,
    coupon?: T_Coupon,
    variant?: string,
    isAbandoned?: boolean,
    packageType?: string
  ) {
    let total = order.orderTotals.find(ot => ot.className === 'ot_total');
    this.track(SegmentECommerceEvents.CHECKOUT_STARTED, {
      cart_type: isUpgrade ? AFFILIATION.UPG : AFFILIATION.ACQ,
      package_type: packageType,
      is_abandoned: isAbandoned ?? false,
      coupon: coupon?.couponCode,
      value: Number((total?.value ?? 0).toFixed(2)),
      products: order?.orderProducts?.map(op => ({
        brand: SKY_BRAND,
        category: useCategory(op?.categoryId),
        name: this.getProductName(op?.product?.name, op?.product?.sku),
        price: op?.price,
        quantity: op?.quantity,
        id: op?.product?.sku,
        variant: op?.categoryName,
      })),
      is_segment: 'yes',
    });
  }

  simulationProductAdded(product: T_OrderProduct, isUpgrade: boolean, quantity = 1) {
    this.track(SegmentECommerceEvents.PRODUCT_ADDED, {
      cart_type: isUpgrade ? AFFILIATION.UPG : AFFILIATION.ACQ,
      package_type: useCategory(product.categoryId),
      brand: SKY_BRAND,
      category: useCategory(product.categoryId),
      name: this.getProductName(product.product?.name, product.product?.sku),
      price: product.price,
      quantity,
      id: product.product?.sku,
      variant: product.categoryName,
      is_segment: 'yes',
    });
  }

  // Simulation is defined as a customer going straight to Checkout.
  simulationProductsAdded(order: T_Order, isUpgrade: boolean, coupon?: T_Coupon, variant?: string) {
    order.orderProducts.forEach(product => {
      this.simulationProductAdded(product, isUpgrade, 1);
    });
  }

  // 1.  Logged in User returns to broadband cart with items in their cart and begins checkout
  // 2.  User logs into their account, navigates to My Account > Manage Subscription and saved items are in the cart from a previous session
  returnToAbandonedCart(orderProducts: T_OrderProduct[], isUpgrade: boolean, whichCart: string, packageType?: string) {
    this.track(
      whichCart === SPAs.MYACCOUNT ? SegmentECommerceEvents.RETURN_TO_CART : SegmentECommerceEvents.PRODUCT_ADDED,
      {
        cart_type: isUpgrade ? AFFILIATION.UPG : AFFILIATION.ACQ,
        package_type: packageType,
        is_abandoned: true,
        products: orderProducts.map(op => ({
          brand: SKY_BRAND,
          category: useCategory(op.categoryId),
          name: this.getProductName(op.product?.name, op.product?.sku),
          price: op.product.price1,
          quantity: op.quantity,
          id: op.product?.sku,
          variant: op.categoryName,
        })),
        is_segment: 'yes',
      }
    );
  }

  // VTV User Completes Order and lands on the order confirmation page.
  vtvMigrationCompleted() {
    this.track(SegmentECommerceEvents.VTV_MIGRATION_COMPLETE, {});
  }

  // User Completes Order and lands on the order confirmation page.
  orderCompleted(
    workOrderNumber: string,
    isUpgrade: boolean,
    order: T_Order,
    coupons?: T_Coupon[],
    packageType?: string,
    accountNumber?: string
  ) {
    let total = order.orderTotals.find(ot => ot.className === 'ot_total');
    this.track(SegmentECommerceEvents.ORDER_COMPLETED, {
      cart_type: isUpgrade ? AFFILIATION.UPG : AFFILIATION.ACQ,
      package_type: packageType,
      coupon: coupons?.map(coupon => coupon.couponCode).join(','),
      value: Number((total?.value ?? 0).toFixed(2)),
      order_id: workOrderNumber,
      subscriber_account_number: accountNumber,
      products: order.orderProducts.map(op => ({
        brand: SKY_BRAND,
        category: useCategory(op.categoryId),
        name: this.getProductName(op.product?.name, op.product?.sku),
        price: op.product.priceIncTax,
        quantity: op.quantity,
        id: op.product?.sku,
        variant: op.categoryName,
      })),
      is_segment: 'yes',
    });
  }

  prequalAddressChecked(isUpgrade: boolean, addressResult: string) {
    this.track(SegmentECommerceEvents.EVENT_PREQUAL_ADDRESS_CHECK, {
      availability_status: addressResult,
    });
  }

  promotionClicked(promotionButtonText: string, promotionCreative: string, promotionName: string) {
    this.track(SegmentECommerceEvents.PROMOTION_CLICKED, {
      promotion_button_text: promotionButtonText,
      promotion_creative: promotionCreative,
      promotion_name: promotionName,
      is_segment: 'yes',
    });
  }
  //CTA Which directs to another URL (track 'Cta Clicked', URL and button label)
  trackLabelAndUrl(navigationCtaText: string, navigationCtaURL: string, context?: string) {
    this.track(SegmentECommerceEvents.LABEL_AND_URL, {
      link_text: navigationCtaText,
      link_url: navigationCtaURL,
      ...(context ? { context } : {}),
    });
  }

  // User Signs In
  signedIn(accountNumber: string) {
    this.track(SegmentECommerceEvents.SIGNED_IN, { subscriber_account_number: accountNumber });
  }

  //checkout step 1, user clicks 'next' on checkout/account-form page
  checkoutStepCompleted(
    isUpgrade: boolean,
    order: T_Order,
    paymentMethod: string,
    step: number,
    coupons?: T_Coupon[],
    packageType?: string
  ) {
    let total = order.orderTotals.find(ot => ot.className === 'ot_total');
    this.track(SegmentECommerceEvents.CHECKOUT_STEP_COMPLETED, {
      cart_type: isUpgrade ? AFFILIATION.UPG : AFFILIATION.ACQ,
      package_type: packageType,
      coupon: coupons?.map(coupon => coupon.couponCode).join(','),
      value: Number((total?.value ?? 0).toFixed(2)),
      payment_method: paymentMethod,
      step: step,
      products: order.orderProducts.map(op => ({
        brand: SKY_BRAND,
        category: useCategory(op.categoryId),
        name: this.getProductName(op.product?.name, op.product?.sku),
        price: op.product.priceIncTax,
        quantity: op.quantity,
        id: op.product?.sku,
        variant: op.categoryName,
      })),
    });
  }

  //User successfully enters credit card details via payment express and is taken to confirmation page
  paymentInfoEntered(
    workOrderNumber: string,
    isUpgrade: boolean,
    paymentMethod: string,
    order?: T_Order,
    coupons?: T_Coupon[],
    packageType?: string
  ) {
    let total = order?.orderTotals.find(ot => ot.className === 'ot_total');
    this.track(SegmentECommerceEvents.PAYMENT_INFO_ENTERED, {
      cart_type: isUpgrade ? AFFILIATION.UPG : AFFILIATION.ACQ,
      package_type: packageType,
      coupon: coupons?.map(coupon => coupon.couponCode).join(','),
      value: Number((total?.value ?? 0).toFixed(2)),
      payment_method: paymentMethod,
      order_id: workOrderNumber,
      products: order?.orderProducts.map(op => ({
        brand: SKY_BRAND,
        category: useCategory(op.categoryId),
        name: this.getProductName(op.product?.name, op.product?.sku),
        price: op.product.priceIncTax,
        quantity: op.quantity,
        id: op.product?.sku,
        variant: op.categoryName,
      })),
    });
  }

  trackQuiz(
    event: string,
    quizStepHeader: string,
    quizInteractionTypes: string,
    quizInteractionDetails: string | null,
    quizSelections: string | string[],
    quizProgressStatus: string,
    quizRecommendedPackage: string,
    quizRecommendedProducts: string | undefined
  ) {
    this.track(event, {
      quiz_step_header: quizStepHeader,
      quiz_interaction_type: quizInteractionTypes,
      quiz_interaction_details: quizInteractionDetails,
      quiz_selections: quizSelections,
      quiz_progress_status: quizProgressStatus,
      quiz_recommended_package: quizRecommendedPackage,
      quiz_recommended_products: quizRecommendedProducts,
    });
  }

  faultSwapBtnClick(btnText: string) {
    this.track(FaultSwapEvents.BUTTON_CLICK, { button_text: btnText });
  }

  buttonClick({name, context}: {name: string, context: string}) {
    this.track(EventNames.BUTTON_CLICK, { name, context });
  }

  popupViewed({name, context}: {name: string, context: string}) {
    this.track(EventNames.POPUP_VIEW, { name, context });
  }
  productSelected({name, sku, context}: {name: string, sku: string, context: string}) {
    this.track(EventNames.PRODUCT_SELECTED, { name, sku, context });
  }

  trackIssuesWithDevice(isKeywordFound: boolean, errorDescription: string) {
    this.track(FaultSwapEvents.TRACK_ISSUES_WITH_THE_DEVICE, {
      is_keyword_found: isKeywordFound,
      error_description: errorDescription,
    });
  }

  faultSwapOrderCompleted(
    workOrderNumber: string,
    accountNumber: string,
    deviceRequestType: string,
    deviceType: string,
    deviceName: string
  ) {
    this.track(FaultSwapEvents.ORDER_COMPLETED, {
      order_id: workOrderNumber,
      subscriber_account_number: accountNumber,
      device_request_type: deviceRequestType,
      device_type: deviceType,
      device_name: deviceName,
    });
  }

  toggleClicked(actionOrigin: string, toggleState: string, toggleType: string) {
    this.track(SegmentECommerceEvents.TOGGLE_CLICK, {
      actionOrigin: actionOrigin,
      toggleState: toggleState,
      toggleType: toggleType,
    });
  }

  bookATechCancel(visitDate: string, visitTime: string, account_id: string, alertMessage: string) {
    this.track(BookATechEvents.confirm_cancellation_clicked, {
      visitDate: visitDate,
      visitTime: visitTime,
      account_id: account_id,
      alertMessage: alertMessage,
    });
  }

  bookATechRechedule(visitDate: string, visitTime: string, account_id: string, alertMessage: string) {
    this.track(BookATechEvents.Rechedule, {
      visitDate: visitDate,
      visitTime: visitTime,
      account_id: account_id,
      alertMessage: alertMessage,
    });
  }

  bookATech_Tech_Service_Continue(bookingReason: string, account_id: string) {
    this.track(BookATechEvents.tech_service_continue_clicked, {
      bookingReason: bookingReason,
      account_id: account_id,
    });
  }
  bookATech_Confirm_Details(account_id: string) {
    this.track(BookATechEvents.Confirm_Details, {
      account_id: account_id,
    });
  }
  bookATechSubmit(visitDate: string, visitTime: string, account_id: string, alertMessage: string) {
    this.track(BookATechEvents.Submit, {
      visitDate: visitDate,
      visitTime: visitTime,
      account_id: account_id,
      alertMessage: alertMessage,
    });
  }
  SatelliteMigrationSubmit(accountNumber: string, toggleState: string, alertMessage: string) {
    this.track(SatelliteMigrationEvent.verification_submit_clicked, {
      accountNumber: accountNumber,
      toggleState: toggleState,
      alertMessage: alertMessage,
    });
  }
  SatelliteMigrationPrizeClicked(linkText: string, linkUrl: string) {
    this.track(SatelliteMigrationEvent.prize_link_clicked, {
      linkText: linkText,
      linkUrl: linkUrl,
    });
  }
  SatelliteMigrationBookTechClicked(linkText: string, linkUrl: string) {
    this.track(SatelliteMigrationEvent.book_tech_clicked, {
      linkText: linkText,
      linkUrl: linkUrl,
    });
  }
  trackSegmentEvent(event: string, properties: any) {
    this.track(event, properties);
  }
}

let segment = new SClient();
export { segment };
